import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Menu, X, Phone, User, LogIn } from "lucide-react";
import { useAuth } from "@/lib/auth";
import Logo from "@/components/landing/Logo";

const navLinks = [
  { name: "Home", href: "#" },
  { name: "Services", href: "/services", route: true },
  { name: "Ceramic", href: "/ceramic", route: true },
  { name: "Portfolio", href: "/portfolio", route: true },
  { name: "Reviews", href: "#reviews" },
  { name: "Contact", href: "#contact" },
];

const Header = ({ onQuoteClick }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (href) => {
    if (href === "#") {
      if (window.location.pathname !== "/") navigate("/");
      else window.scrollTo({ top: 0, behavior: "smooth" });
    } else if (href.startsWith("/")) {
      navigate(href);
    } else {
      // Hash links — if not on landing page, navigate there first
      if (window.location.pathname !== "/") {
        navigate("/" + href);
      } else {
        const element = document.querySelector(href);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <motion.header
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled
          ? "bg-black/90 backdrop-blur-xl border-b border-sky-500/20"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Logo */}
          <motion.a
            href="#"
            data-testid="header-logo"
            className="flex items-center group"
            whileHover={{ scale: 1.02 }}
            onClick={(e) => { e.preventDefault(); scrollToSection("#"); }}
          >
            {/* Smaller logo on mobile, full size on desktop */}
            <div className="block sm:hidden">
              <Logo size={36} showWordmark />
            </div>
            <div className="hidden sm:block">
              <Logo size={52} showWordmark />
            </div>
          </motion.a>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link, index) => (
              <motion.button
                key={link.name}
                onClick={() => scrollToSection(link.href)}
                data-testid={`nav-${link.name.toLowerCase()}`}
                className="relative px-4 py-2 text-gray-400 hover:text-white transition-colors text-sm font-medium tracking-wide group"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -2 }}
              >
                {link.name}
                <motion.div
                  className="absolute bottom-0 left-1/2 -translate-x-1/2 h-[2px] bg-sky-500"
                  initial={{ width: 0 }}
                  whileHover={{ width: "60%" }}
                  transition={{ duration: 0.3 }}
                />
              </motion.button>
            ))}
          </nav>

          {/* CTA */}
          <div className="hidden lg:flex items-center gap-6">
            <motion.a
              href="tel:5203804407"
              data-testid="header-phone"
              className="flex items-center gap-2 text-gray-400 hover:text-sky-400 transition-colors group"
              whileHover={{ scale: 1.05 }}
            >
              <Phone className="w-4 h-4" />
              <span className="text-sm">(520) 380-4407</span>
            </motion.a>
            {user ? (
              <button
                data-testid="header-account"
                onClick={() => navigate("/account")}
                className="flex items-center gap-2 text-gray-300 hover:text-white transition-colors"
              >
                {user.picture ? (
                  <img src={user.picture} alt="" className="w-7 h-7 rounded-full" />
                ) : (
                  <User className="w-4 h-4" />
                )}
                <span className="text-sm">{user.name?.split(" ")[0] || "Account"}</span>
              </button>
            ) : (
              <button
                data-testid="header-login"
                onClick={() => navigate("/login")}
                className="flex items-center gap-2 text-gray-300 hover:text-white transition-colors text-sm"
              >
                <LogIn className="w-4 h-4" />
                Login
              </button>
            )}
            <button
              data-testid="header-cta"
              onClick={onQuoteClick}
              className="bg-sky-500 hover:bg-sky-400 text-white px-5 py-2 rounded-lg text-sm font-medium transition-colors"
            >
              Get Quote
            </button>
          </div>

          {/* Mobile Menu Button */}
          <motion.button
            data-testid="mobile-menu-toggle"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden text-white p-2"
            whileTap={{ scale: 0.95 }}
          >
            <AnimatePresence mode="wait">
              {isMobileMenuOpen ? (
                <motion.div
                  key="close"
                  initial={{ rotate: -90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: 90, opacity: 0 }}
                >
                  <X className="w-6 h-6" />
                </motion.div>
              ) : (
                <motion.div
                  key="menu"
                  initial={{ rotate: 90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: -90, opacity: 0 }}
                >
                  <Menu className="w-6 h-6" />
                </motion.div>
              )}
            </AnimatePresence>
          </motion.button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-black/95 backdrop-blur-xl border-t border-sky-500/20 max-h-[calc(100svh-80px)] overflow-y-auto"
          >
            <div className="px-6 py-6 space-y-2">
              {navLinks.map((link, index) => (
                <motion.button
                  key={link.name}
                  onClick={() => scrollToSection(link.href)}
                  className="block w-full text-left text-gray-300 hover:text-sky-400 py-3 text-lg border-b border-white/5"
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: index * 0.05 }}
                >
                  {link.name}
                </motion.button>
              ))}
              <div className="pt-4">
                <button
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    onQuoteClick?.();
                  }}
                  className="w-full bg-sky-500 hover:bg-sky-400 text-white py-3 rounded-lg font-medium"
                >
                  Get a Quote Now
                </button>
                {user ? (
                  <button
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      navigate("/account");
                    }}
                    className="w-full mt-2 border border-white/20 text-white py-3 rounded-lg font-medium"
                  >
                    My Account
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      navigate("/login");
                    }}
                    className="w-full mt-2 border border-white/20 text-white py-3 rounded-lg font-medium"
                  >
                    Login / Create account
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Header;
